/**
 * Regras puras da criação (Fase 5): validação do rascunho para publicar, UTMs, segmentação no
 * formato da Meta, matriz da criação em massa, nomes, normalização de CSV (BR) e templates.
 * Sem I/O: o hash SHA-256 fica no servidor.
 */
import { minDailyBudgetCents } from "./campaign-enums.js";
import {
  BULK_MAX_ADS,
  CAROUSEL_MAX_CARDS,
  CAROUSEL_MIN_CARDS,
  destinationOption,
  type DestinationType,
  type OptimizationGoal,
} from "./creation-enums.js";
import type {
  AdDraft,
  AdSetDraft,
  BulkMatrixInput,
  PlacementsDraft,
  TargetingDraft,
  UtmDraft,
  WizardDraft,
} from "./creation-schemas.js";

export interface DraftIssue {
  /** Caminho no rascunho (ex.: `adsets.0.ads.1.link`). */
  path: string;
  message: string;
}

// ---- UTMs -----------------------------------------------------------------------------------------

export interface UtmContext {
  campaignName: string;
  adsetName: string;
  adName: string;
}

/** Substitui `{{campaign.name}}`, `{{adset.name}}` e `{{ad.name}}` nos valores. */
export function resolveUtmValue(value: string, ctx: UtmContext): string {
  return value
    .replaceAll("{{campaign.name}}", ctx.campaignName)
    .replaceAll("{{adset.name}}", ctx.adsetName)
    .replaceAll("{{ad.name}}", ctx.adName);
}

/** `url_tags` do criativo: `utm_source=...&utm_medium=...` (vazios ficam de fora). */
export function buildUrlTags(utm: UtmDraft, ctx: UtmContext): string {
  const pairs: [string, string][] = [
    ["utm_source", utm.source],
    ["utm_medium", utm.medium],
    ["utm_campaign", utm.campaign],
    ["utm_content", utm.content],
    ["utm_term", utm.term],
  ];
  const params = new URLSearchParams();
  for (const [k, v] of pairs) {
    const resolved = resolveUtmValue(v, ctx).trim();
    if (resolved) params.set(k, resolved);
  }
  return params.toString();
}

/** Link final com os UTMs aplicados (para mostrar na prévia/revisão). */
export function linkWithUtm(
  link: string,
  utm: UtmDraft,
  enabled: boolean,
  ctx: UtmContext,
): string {
  if (!enabled || !link) return link;
  const tags = buildUrlTags(utm, ctx);
  if (!tags) return link;
  return link + (link.includes("?") ? "&" : "?") + tags;
}

// ---- Segmentação no formato da Meta ------------------------------------------------------------

export interface MetaTargetingSpec {
  geo_locations: {
    countries?: string[];
    regions?: { key: string }[];
    cities?: { key: string; radius?: number; distance_unit?: "kilometer" }[];
  };
  age_min: number;
  age_max: number;
  genders?: number[];
  flexible_spec?: { interests: { id: string; name: string }[] }[];
  custom_audiences?: { id: string }[];
  excluded_custom_audiences?: { id: string }[];
  targeting_automation?: { advantage_audience: 0 | 1 };
  publisher_platforms?: string[];
  facebook_positions?: string[];
  instagram_positions?: string[];
  messenger_positions?: string[];
  audience_network_positions?: string[];
}

export function targetingToMeta(
  t: TargetingDraft,
  placements?: PlacementsDraft,
): MetaTargetingSpec {
  const spec: MetaTargetingSpec = {
    geo_locations: {},
    age_min: t.ageMin,
    age_max: t.ageMax,
  };
  if (t.countries.length > 0) spec.geo_locations.countries = [...t.countries];
  if (t.regions.length > 0) spec.geo_locations.regions = t.regions.map((r) => ({ key: r.key }));
  if (t.cities.length > 0) {
    spec.geo_locations.cities = t.cities.map((c) =>
      c.radiusKm > 0
        ? { key: c.key, radius: c.radiusKm, distance_unit: "kilometer" as const }
        : { key: c.key },
    );
  }
  if (t.genders.length > 0) spec.genders = [...t.genders];
  if (t.interests.length > 0) {
    spec.flexible_spec = [{ interests: t.interests.map((i) => ({ id: i.id, name: i.name })) }];
  }
  if (t.customAudiences.length > 0)
    spec.custom_audiences = t.customAudiences.map((a) => ({ id: a.id }));
  if (t.excludedCustomAudiences.length > 0) {
    spec.excluded_custom_audiences = t.excludedCustomAudiences.map((a) => ({ id: a.id }));
  }
  spec.targeting_automation = { advantage_audience: t.advantageAudience ? 1 : 0 };
  if (placements && !placements.automatic) {
    if (placements.publisherPlatforms.length > 0)
      spec.publisher_platforms = [...placements.publisherPlatforms];
    if (placements.facebookPositions.length > 0)
      spec.facebook_positions = [...placements.facebookPositions];
    if (placements.instagramPositions.length > 0)
      spec.instagram_positions = [...placements.instagramPositions];
    if (placements.messengerPositions.length > 0)
      spec.messenger_positions = [...placements.messengerPositions];
    if (placements.audienceNetworkPositions.length > 0)
      spec.audience_network_positions = [...placements.audienceNetworkPositions];
  }
  return spec;
}

/** Caminho inverso: segmentação da Meta → rascunho (duplicação entre contas, públicos salvos). */
export function targetingFromMeta(raw: unknown): {
  targeting: TargetingDraft;
  placements: PlacementsDraft;
} {
  const o = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  const geo = (
    typeof o.geo_locations === "object" && o.geo_locations !== null ? o.geo_locations : {}
  ) as Record<string, unknown>;
  const list = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
  const named = (v: unknown) =>
    list<{ id?: unknown; name?: unknown }>(v).flatMap((x) =>
      typeof x.id === "string"
        ? [{ id: x.id, name: typeof x.name === "string" ? x.name : x.id }]
        : [],
    );
  const flexible = list<{ interests?: unknown }>(o.flexible_spec).flatMap((f) =>
    named(f.interests),
  );
  const num = (v: unknown, d: number) => (typeof v === "number" ? v : d);
  const automation = (
    typeof o.targeting_automation === "object" && o.targeting_automation !== null
      ? o.targeting_automation
      : {}
  ) as { advantage_audience?: unknown };
  const strings = (v: unknown) =>
    list<unknown>(v).filter((x): x is string => typeof x === "string");
  const publisher = strings(o.publisher_platforms);
  const targeting: TargetingDraft = {
    countries: strings(geo.countries),
    regions: list<{ key?: unknown; name?: unknown }>(geo.regions).flatMap((r) =>
      typeof r.key === "string"
        ? [
            {
              key: r.key,
              name: typeof r.name === "string" ? r.name : r.key,
              type: "region" as const,
              countryCode: "",
              radiusKm: 0,
            },
          ]
        : [],
    ),
    cities: list<{ key?: unknown; name?: unknown; radius?: unknown }>(geo.cities).flatMap((c) =>
      typeof c.key === "string"
        ? [
            {
              key: c.key,
              name: typeof c.name === "string" ? c.name : c.key,
              type: "city" as const,
              countryCode: "",
              radiusKm: typeof c.radius === "number" ? Math.round(c.radius) : 0,
            },
          ]
        : [],
    ),
    ageMin: num(o.age_min, 18),
    ageMax: num(o.age_max, 65),
    genders: list<unknown>(o.genders).filter((g): g is 1 | 2 => g === 1 || g === 2),
    interests: flexible,
    customAudiences: named(o.custom_audiences),
    excludedCustomAudiences: named(o.excluded_custom_audiences),
    advantageAudience: automation.advantage_audience === 1,
  };
  const placements: PlacementsDraft = {
    automatic: publisher.length === 0,
    publisherPlatforms: publisher.filter(
      (p): p is PlacementsDraft["publisherPlatforms"][number] =>
        p === "facebook" || p === "instagram" || p === "messenger" || p === "audience_network",
    ),
    facebookPositions: strings(o.facebook_positions),
    instagramPositions: strings(o.instagram_positions),
    messengerPositions: strings(o.messenger_positions),
    audienceNetworkPositions: strings(o.audience_network_positions),
  };
  return { targeting, placements };
}

// ---- Validação para publicar ---------------------------------------------------------------------

function isHttpUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
}

function validateAd(ad: AdDraft, path: string, needsLink: boolean, issues: DraftIssue[]): void {
  if (!ad.name.trim()) issues.push({ path: `${path}.name`, message: "Dê um nome ao anúncio." });
  if (!ad.pageId)
    issues.push({ path: `${path}.pageId`, message: "Escolha a página que assina o anúncio." });
  if (!ad.primaryText.trim())
    issues.push({ path: `${path}.primaryText`, message: "Escreva o texto principal." });
  if (needsLink) {
    if (!ad.link.trim())
      issues.push({ path: `${path}.link`, message: "Informe a URL de destino." });
    else if (!isHttpUrl(ad.link.trim()))
      issues.push({
        path: `${path}.link`,
        message: "A URL precisa começar com https:// ou http://.",
      });
  }
  if (ad.format === "imagem" && !ad.imageAssetId)
    issues.push({ path: `${path}.imageAssetId`, message: "Escolha uma imagem da biblioteca." });
  if (ad.format === "video" && !ad.videoAssetId)
    issues.push({ path: `${path}.videoAssetId`, message: "Escolha um vídeo da biblioteca." });
  if (ad.format === "carrossel") {
    if (ad.cards.length < CAROUSEL_MIN_CARDS || ad.cards.length > CAROUSEL_MAX_CARDS) {
      issues.push({
        path: `${path}.cards`,
        message: `O carrossel precisa ter de ${CAROUSEL_MIN_CARDS} a ${CAROUSEL_MAX_CARDS} cartões.`,
      });
    }
    ad.cards.forEach((card, i) => {
      if (!card.assetId)
        issues.push({ path: `${path}.cards.${i}.assetId`, message: "Escolha a mídia do cartão." });
      if (card.link && !isHttpUrl(card.link))
        issues.push({ path: `${path}.cards.${i}.link`, message: "URL do cartão inválida." });
    });
  }
}

function validateAdSet(
  adset: AdSetDraft,
  path: string,
  draft: WizardDraft,
  currency: string,
  issues: DraftIssue[],
): void {
  if (!adset.name.trim()) issues.push({ path: `${path}.name`, message: "Dê um nome ao conjunto." });
  const option = destinationOption(draft.campaign.objective, adset.destinationType);
  if (!option) {
    issues.push({
      path: `${path}.destinationType`,
      message: "Este local de conversão não é compatível com o objetivo da campanha.",
    });
  } else {
    if (!option.goals.includes(adset.optimizationGoal)) {
      issues.push({
        path: `${path}.optimizationGoal`,
        message: "Esta otimização não é compatível com o local de conversão escolhido.",
      });
    }
    if (option.needsPixel) {
      if (!adset.pixelId) issues.push({ path: `${path}.pixelId`, message: "Escolha o pixel." });
      if (!adset.customEventType)
        issues.push({ path: `${path}.customEventType`, message: "Escolha o evento de conversão." });
    }
    if (option.needsPage && !adset.pageId)
      issues.push({ path: `${path}.pageId`, message: "Escolha a página." });
    if (option.needsApp && (!adset.applicationId || !adset.objectStoreUrl)) {
      issues.push({
        path: `${path}.applicationId`,
        message: "Informe o id do app e a URL da loja (promoted_object).",
      });
    }
  }
  if (draft.campaign.budgetMode === "ABO") {
    const min = minDailyBudgetCents(currency);
    if (adset.budgetCents < min) {
      issues.push({
        path: `${path}.budgetCents`,
        message: `O orçamento do conjunto precisa ser de pelo menos ${(min / 100).toFixed(2)} ${currency}.`,
      });
    }
    if (adset.budgetKind === "vitalicio" && !adset.endTime)
      issues.push({
        path: `${path}.endTime`,
        message: "Orçamento vitalício exige data de término.",
      });
  }
  if (
    (adset.bidAmountCents === null || adset.bidAmountCents <= 0) &&
    (draft.campaign.bidStrategy === "LOWEST_COST_WITH_BID_CAP" ||
      draft.campaign.bidStrategy === "COST_CAP")
  ) {
    issues.push({
      path: `${path}.bidAmountCents`,
      message: "A estratégia de lance escolhida exige um valor de lance.",
    });
  }
  if (adset.startTime && adset.endTime && adset.endTime <= adset.startTime)
    issues.push({ path: `${path}.endTime`, message: "O término precisa ser depois do início." });
  const t = adset.targeting;
  if (t.countries.length === 0 && t.regions.length === 0 && t.cities.length === 0)
    issues.push({ path: `${path}.targeting.countries`, message: "Escolha pelo menos um local." });
  if (t.ageMin > t.ageMax)
    issues.push({ path: `${path}.targeting.ageMin`, message: "Idade mínima maior que a máxima." });
  if (!adset.placements.automatic && adset.placements.publisherPlatforms.length === 0) {
    issues.push({
      path: `${path}.placements.publisherPlatforms`,
      message: "Escolha pelo menos uma plataforma ou use os posicionamentos automáticos.",
    });
  }
  if (adset.ads.length === 0)
    issues.push({ path: `${path}.ads`, message: "Adicione pelo menos um anúncio." });
  const needsLink = adset.destinationType === "WEBSITE" || adset.destinationType === "APP";
  adset.ads.forEach((ad, i) => validateAd(ad, `${path}.ads.${i}`, needsLink, issues));
}

/** Tudo que precisa estar certo antes de criar na Meta (espelha as regras da referência v26.0). */
export function validateDraftForPublish(draft: WizardDraft, currency: string): DraftIssue[] {
  const issues: DraftIssue[] = [];
  const c = draft.campaign;
  if (!c.name.trim()) issues.push({ path: "campaign.name", message: "Dê um nome à campanha." });
  if (c.budgetMode === "CBO") {
    const min = minDailyBudgetCents(currency);
    if (c.budgetCents < min) {
      issues.push({
        path: "campaign.budgetCents",
        message: `O orçamento da campanha precisa ser de pelo menos ${(min / 100).toFixed(2)} ${currency}.`,
      });
    }
    if (c.budgetKind === "vitalicio" && draft.adsets.some((a) => !a.endTime)) {
      issues.push({
        path: "campaign.budgetKind",
        message: "Orçamento vitalício exige data de término em todos os conjuntos.",
      });
    }
  }
  if (draft.adsets.length === 0)
    issues.push({ path: "adsets", message: "Adicione pelo menos um conjunto de anúncios." });
  const totalAds = draft.adsets.reduce((n, a) => n + a.ads.length, 0);
  if (totalAds > BULK_MAX_ADS) {
    issues.push({
      path: "adsets",
      message: `Limite de segurança: no máximo ${BULK_MAX_ADS} anúncios por publicação (há ${totalAds}).`,
    });
  }
  draft.adsets.forEach((adset, i) => validateAdSet(adset, `adsets.${i}`, draft, currency, issues));
  return issues;
}

// ---- Criação em massa ----------------------------------------------------------------------------

export interface BulkPlan {
  draft: WizardDraft;
  adsetCount: number;
  adCount: number;
  /** Linhas da tabela de prévia (uma por anúncio). */
  rows: { adsetName: string; adName: string; audience: string; creative: string; text: string }[];
  overLimit: boolean;
}

function slug(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

/** Aplica um padrão com tokens `{campanha}`, `{publico}`, `{criativo}`, `{texto}`, `{n}`. */
export function applyNamePattern(
  pattern: string,
  tokens: { campanha: string; publico: string; criativo: string; texto: string; n: number },
): string {
  return slug(
    pattern
      .replaceAll("{campanha}", tokens.campanha)
      .replaceAll("{publico}", tokens.publico)
      .replaceAll("{criativo}", tokens.criativo)
      .replaceAll("{texto}", tokens.texto)
      .replaceAll("{n}", String(tokens.n)),
  ).slice(0, 400);
}

/** Matriz públicos × criativos × textos → rascunho pronto para o motor de publicação. */
export function buildBulkPlan(matrix: BulkMatrixInput): BulkPlan {
  const rows: BulkPlan["rows"] = [];
  let n = 0;
  const adsets: AdSetDraft[] = matrix.audiences.map((audience, ai) => {
    const adsetName = applyNamePattern(matrix.adsetNamePattern, {
      campanha: matrix.campaign.name,
      publico: audience.label,
      criativo: "",
      texto: "",
      n: ai + 1,
    });
    const ads: AdDraft[] = [];
    for (const creative of matrix.creatives) {
      for (const text of matrix.texts) {
        n += 1;
        const adName = applyNamePattern(matrix.adNamePattern, {
          campanha: matrix.campaign.name,
          publico: audience.label,
          criativo: creative.label,
          texto: text.label,
          n,
        });
        ads.push({
          ...matrix.adBase,
          key: `m${ai + 1}-${n}`,
          name: adName,
          format: creative.format,
          primaryText: text.primaryText,
          headline: text.headline,
          description: text.description,
          imageAssetId: creative.format === "imagem" ? creative.assetId : null,
          videoAssetId: creative.format === "video" ? creative.assetId : null,
          cards: [],
        });
        rows.push({
          adsetName,
          adName,
          audience: audience.label,
          creative: creative.label,
          text: text.label,
        });
      }
    }
    return {
      ...matrix.adsetBase,
      key: `a${ai + 1}`,
      name: adsetName,
      targeting: audience.targeting,
      ads,
    };
  });
  return {
    draft: { campaign: matrix.campaign, adsets },
    adsetCount: adsets.length,
    adCount: n,
    rows,
    overLimit: n > BULK_MAX_ADS,
  };
}

// ---- CSV de clientes (normalização BR; o hash fica no servidor) -------------------------------

export interface CustomerRow {
  email: string | null;
  phone: string | null;
}

export interface ParsedCustomerCsv {
  rows: CustomerRow[];
  /** Linhas sem e-mail nem telefone válidos. */
  invalid: number;
  columns: { email: number | null; phone: number | null };
  total: number;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Minúsculas, sem espaços; null se não parecer e-mail. */
export function normalizeEmail(value: string): string | null {
  const v = value.trim().toLowerCase();
  return EMAIL_RE.test(v) ? v : null;
}

/**
 * Telefone no formato que a Meta espera: só dígitos com DDI. Números brasileiros (10–11 dígitos
 * com DDD, com ou sem 0 à esquerda) recebem o 55. Devolve null se não houver 8+ dígitos.
 */
export function normalizePhoneBR(value: string): string | null {
  let digits = value.replace(/\D/g, "");
  if (!digits) return null;
  if (value.trim().startsWith("+")) return digits.length >= 8 ? digits : null;
  digits = digits.replace(/^0+/, "");
  if (digits.length === 10 || digits.length === 11) return `55${digits}`;
  if (digits.length === 12 || digits.length === 13) {
    return digits.startsWith("55") ? digits : null;
  }
  return digits.length >= 8 ? digits : null;
}

function splitCsvLine(line: string, sep: string): string[] {
  const out: string[] = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i] ?? "";
    if (ch === '"') {
      if (quoted && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else quoted = !quoted;
    } else if (ch === sep && !quoted) {
      out.push(cur);
      cur = "";
    } else cur += ch;
  }
  out.push(cur);
  return out.map((c) => c.trim());
}

/** Lê um CSV (`;`, `,` ou tab), detecta as colunas de e-mail/telefone e normaliza cada linha. */
export function parseCustomerCsv(text: string): ParsedCustomerCsv {
  const lines = text
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .filter((l) => l.trim().length > 0);
  if (lines.length === 0)
    return { rows: [], invalid: 0, columns: { email: null, phone: null }, total: 0 };
  const first = lines[0] ?? "";
  const sep = [";", ",", "\t"].reduce((best, s) =>
    first.split(s).length > first.split(best).length ? s : best,
  );
  const header = splitCsvLine(first, sep).map((h) => h.toLowerCase());
  const emailCol = header.findIndex((h) => /e-?mail/.test(h));
  const phoneCol = header.findIndex((h) => /(telefone|phone|celular|whatsapp|fone)/.test(h));
  const hasHeader = emailCol >= 0 || phoneCol >= 0;
  const body = hasHeader ? lines.slice(1) : lines;
  const rows: CustomerRow[] = [];
  let invalid = 0;
  for (const line of body) {
    const cells = splitCsvLine(line, sep);
    let email: string | null = null;
    let phone: string | null = null;
    if (hasHeader) {
      if (emailCol >= 0) email = normalizeEmail(cells[emailCol] ?? "");
      if (phoneCol >= 0) phone = normalizePhoneBR(cells[phoneCol] ?? "");
    } else {
      // Sem cabeçalho: cada célula é testada como e-mail, senão como telefone.
      for (const cell of cells) {
        if (!email && cell.includes("@")) email = normalizeEmail(cell);
        else if (!phone && /\d{8,}/.test(cell.replace(/\D/g, ""))) phone = normalizePhoneBR(cell);
      }
    }
    if (!email && !phone) {
      invalid += 1;
      continue;
    }
    rows.push({ email, phone });
  }
  return {
    rows,
    invalid,
    columns: { email: emailCol >= 0 ? emailCol : null, phone: phoneCol >= 0 ? phoneCol : null },
    total: body.length,
  };
}

// ---- Templates (sem ids da conta) ---------------------------------------------------------------

/** Remove tudo que é da conta de origem: página, Instagram, pixel, públicos e mídias ficam vazios. */
export function draftToTemplate(draft: WizardDraft): WizardDraft {
  return {
    campaign: { ...draft.campaign },
    adsets: draft.adsets.map((a) => ({
      ...a,
      pixelId: null,
      pageId: null,
      applicationId: null,
      objectStoreUrl: null,
      startTime: null,
      endTime: null,
      targeting: {
        ...a.targeting,
        customAudiences: [],
        excludedCustomAudiences: [],
      },
      ads: a.ads.map((ad) => ({
        ...ad,
        pageId: null,
        instagramUserId: null,
        imageAssetId: null,
        videoAssetId: null,
        thumbnailAssetId: null,
        cards: ad.cards.map((c) => ({ ...c, assetId: null })),
      })),
    })),
  };
}

/** Nome do item recriado em outra conta. */
export function recreatedName(name: string, suffix: string): string {
  return suffix ? `${name}${suffix}` : name;
}

export function goalNeedsLink(destination: DestinationType, _goal: OptimizationGoal): boolean {
  return destination === "WEBSITE" || destination === "APP";
}
